import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { SMART_ROUTES } from '../../constants';
import {
  SmartGalleryChromeMenu,
  SmartHeaderMenu,
  type SmartHeaderMenuProps,
} from './SmartHeaderMenu';
import { useThemeModeStore } from './store/useThemeModeStore';
import { AppReveal } from './ui';
import { cn } from './utils/cn';

interface SmartShellProps {
  children: ReactNode;
  /** Optional wizard headline above the body. */
  title?: string;
  subtitle?: string;
  /** Hide the brand header block (e.g. splash / terminal screens). */
  hideHeader?: boolean;
  brandName?: string;
  /**
   * Home uses looser chrome (mb-8 header, pt-10 footer) matching
   * raas-widgets-flow-smart HomePage; wizard keeps the tighter defaults.
   */
  variant?: 'home' | 'wizard';
  /**
   * Header overflow menu (hamburger). When omitted, gallery uses the default
   * chrome menu with notifications + fixture unread.
   */
  headerMenu?: SmartHeaderMenuProps;
}

/**
 * Shared chrome for smart gallery screens — keeps the Tailwind kit layout
 * (max-w-md column + brand header) without AuraLayout.
 */
export const SmartShell = ({
  children,
  title,
  subtitle,
  hideHeader = false,
  brandName,
  variant = 'wizard',
  headerMenu,
}: SmartShellProps) => {
  const { t } = useTranslation('smart');
  const brand = brandName ?? t('home.brand', 'Lola Send');
  const mode = useThemeModeStore((s) => s.mode);
  const setMode = useThemeModeStore((s) => s.setMode);

  // Ensure data-theme is applied on first paint of a smart screen (flow does
  // this in index.html before React mounts).
  useEffect(() => {
    setMode(mode);
  }, [mode, setMode]);

  const isHome = variant === 'home';

  return (
    <div className="smart-root">
      <AppReveal>
        <main className="mx-auto flex min-h-dvh w-full max-w-md flex-col px-5 pb-10 pt-8">
          <header
            className={cn(
              'relative z-40 flex items-center justify-between',
              isHome ? 'mb-8' : 'mb-6',
            )}
          >
            <Link
              to={SMART_ROUTES.HOME}
              aria-label={t('common.home', 'Home')}
              className="text-accent text-lg font-bold uppercase tracking-tight transition-opacity hover:opacity-80"
            >
              {brand}
            </Link>
            {headerMenu ? (
              <SmartHeaderMenu {...headerMenu} />
            ) : (
              <SmartGalleryChromeMenu />
            )}
          </header>

          {!hideHeader && title && (
            <section>
              <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
              {subtitle && (
                <p className="text-fg-muted mt-1 text-sm">{subtitle}</p>
              )}
            </section>
          )}

          <div className={title && !hideHeader ? 'mt-6 flex-1' : 'flex-1'}>
            {children}
          </div>

          <footer
            className={cn(
              'text-fg-muted text-center',
              isHome ? 'pt-10 text-xs' : 'mt-8 text-[11px]',
            )}
          >
            powered by{' '}
            <a
              href="https://leapfinancial.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-fg font-semibold transition-opacity hover:opacity-80"
            >
              Leapfinancial
            </a>
          </footer>
        </main>
      </AppReveal>
    </div>
  );
};
